import React from 'react';
import { getAdminBadge } from '../../utils/getAdminBadge';
import { visibleStatuses, visibleCountingRule, visibleMetrics } from './visitsMethodology.config';
import { VISITS_FEATURES, OPEN_FEATURE_ACCESS } from '../../constants/visitsConfig';

const Card = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 ${className}`}>
    {children}
  </div>
);

// A status / family line: colored dot + label + definition. Admin-only entries
// get the shield badge so it's clear they aren't shown to regular users.
const DefinitionRow = ({ color, label, desc, admin }) => (
  <div className="flex items-start gap-2.5 text-sm">
    <span className="w-3 h-3 rounded-sm flex-none mt-1" style={{ background: color }} />
    <span>
      <span className="font-semibold text-gray-900 dark:text-gray-100">{label}</span>
      {admin && <span className="ml-1 align-middle">{getAdminBadge()}</span>}
      <span className="text-gray-500 dark:text-gray-400"> — {desc}</span>
    </span>
  </div>
);

/**
 * The lexicon is offered to everyone, but part of it defines statuses, metrics
 * and affordances a client is shown nowhere else in the module. `isAdmin` comes
 * from the SIMULATED user, so the client view withholds them too. Which entries
 * those are is decided in visitsMethodology.config.js, not here.
 * `durationsAccess` applies the client's config to the « Durée moyenne » entry.
 */
const VisitsMethodology = ({ isAdmin = false, durationsAccess = OPEN_FEATURE_ACCESS }) => {
  const countingRule = visibleCountingRule(isAdmin);

  return (
    <div className="space-y-6 text-left">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 flex-none">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          </svg>
        </span>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Lexique &amp; Définitions</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-tight">
            Ce que signifient les statuts de visite et comment sont calculés les indicateurs.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Statuts de visite */}
        <Card>
          <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">Statuts de visite</div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Une visite correspond à un formulaire terrain. Son statut détermine comment elle est comptée.
          </p>
          <div className="space-y-2.5">
            {visibleStatuses(isAdmin).map((s) => (
              <DefinitionRow key={s.label} color={s.color} label={s.label} desc={s.desc} admin={s.admin} />
            ))}
          </div>
        </Card>

        {/* Ce qui compte dans les stats */}
        <Card>
          <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">{countingRule.title}</div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{countingRule.text}</p>
          <div className="space-y-2.5">
            {countingRule.excluded.map((e) => (
              <DefinitionRow key={e.label} color={e.color} label={e.label} desc={e.desc} />
            ))}
          </div>
        </Card>
      </div>

      {/* Indicateurs */}
      <div>
        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">Principaux indicateurs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleMetrics(isAdmin, { [VISITS_FEATURES.DURATIONS]: durationsAccess }).map((item) => (
            <Card key={item.name} className="flex flex-col gap-2">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-gray-900 dark:text-white">{item.name}</span>
                {item.admin && getAdminBadge()}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{item.measures}</p>
              <p className="text-[12.5px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/40 rounded-lg px-3 py-2 leading-snug">
                <span className="font-semibold text-gray-600 dark:text-gray-300">Calcul&nbsp;: </span>{item.rule}
              </p>
              {item.note && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  <span className="font-semibold text-gray-600 dark:text-gray-300">Bon à savoir&nbsp;:</span> {item.note}
                </p>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VisitsMethodology;
